import { apiRequest } from '../../shared/api';
import {
  Meeting,
  MeetingType,
  MeetingDashboardKPIs,
  MeetingParticipant,
  MeetingAgenda,
  MeetingAgendaItem,
  MeetingMinutes,
  MeetingDecision,
  MeetingActionBridge,
  ManagementReview,
  ManagementReviewInput,
  MeetingStatusHistory,
  MeetingVersion
} from './types';

export interface SystemUserOption {
  id: string;
  username: string;
  display_name: string;
  email: string;
  role: string;
  department?: string;
}

// Robust fallback seed in case remote server is restarting or unseeded
const FALLBACK_MEETING_TYPES: MeetingType[] = [
  {
    id: 'MT-SRB',
    organization_id: 'ORG-XYZ',
    type_code: 'SRB',
    title: 'Safety Review Board (SRB)',
    category: 'GOVERNANCE',
    default_duration_minutes: 90,
    requires_agenda: true,
    requires_minutes: true,
    requires_quorum: true,
    quorum_percentage: 60,
    is_management_review: false,
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'MT-SAG',
    organization_id: 'ORG-XYZ',
    type_code: 'SAG',
    title: 'Safety Action Group (SAG)',
    category: 'OPERATIONAL',
    default_duration_minutes: 60,
    requires_agenda: true,
    requires_minutes: true,
    requires_quorum: false,
    quorum_percentage: 50,
    is_management_review: false,
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'MT-MR',
    organization_id: 'ORG-XYZ',
    type_code: 'MR',
    title: 'Annual Management Review (MR)',
    category: 'EXECUTIVE',
    default_duration_minutes: 120,
    requires_agenda: true,
    requires_minutes: true,
    requires_quorum: true,
    quorum_percentage: 75,
    is_management_review: true,
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

const FALLBACK_SYSTEM_USERS: SystemUserOption[] = [
  { id: 'usr_erdal_aydeniz', username: 'erdal_aydeniz', display_name: 'Erdal Aydeniz', email: 'erdal_aydeniz@luminixaviation.com', role: 'SAFETY_MANAGER', department: 'Safety & Quality' },
  { id: 'usr_cemil_demir', username: 'cemil_demir', display_name: 'Cemil Demir (Accountable Manager)', email: 'cemil.demir@luminixaviation.com', role: 'ORGANIZATION_ADMIN', department: 'Executive Board' },
  { id: 'usr_burak_demir', username: 'burak_demir', display_name: 'Captain Burak Demir', email: 'burak.demir@luminixaviation.com', role: 'LINE_PILOT', department: 'Flight Operations' },
  { id: 'usr_selin_ozturk', username: 'selin_ozturk', display_name: 'Selin Öztürk', email: 'selin.ozturk@luminixaviation.com', role: 'QUALITY_MANAGER', department: 'Quality Assurance' },
  { id: 'usr_ahmet_yilmaz', username: 'ahmet_yilmaz', display_name: 'Ahmet Yılmaz', email: 'ahmet.yilmaz@luminixaviation.com', role: 'MAINTENANCE_TECH', department: 'Technical & Engineering' },
  { id: 'usr_mehmet_kaya', username: 'mehmet_kaya', display_name: 'Mehmet Kaya', email: 'mehmet.kaya@luminixaviation.com', role: 'MOC_MANAGER', department: 'CAMO' },
  { id: 'usr_zeynep_celik', username: 'zeynep_celik', display_name: 'Zeynep Çelik', email: 'zeynep.celik@luminixaviation.com', role: 'COMPLIANCE_MANAGER', department: 'Compliance & Legal' },
  { id: 'usr_murat_arslan', username: 'murat_arslan', display_name: 'Murat Arslan', email: 'murat.arslan@luminixaviation.com', role: 'TASK_MANAGER', department: 'Ground Handling & Ramp' },
  { id: 'usr_ayse_sen', username: 'ayse_sen', display_name: 'Ayşe Şen', email: 'ayse.sen@luminixaviation.com', role: 'TRAINING_MANAGER', department: 'Cabin Crew Training' },
  { id: 'usr_emre_koc', username: 'emre_koc', display_name: 'Emre Koç', email: 'emre.koc@luminixaviation.com', role: 'SECURITY_OFFICER', department: 'Corporate Security' },
];

const FALLBACK_MEETINGS: Meeting[] = [
  {
    id: 'MTG-2026-001',
    organization_id: 'ORG-XYZ',
    meeting_number: 'MTG-2026-001',
    meeting_type_id: 'MT-SRB',
    title: 'Q2 2026 Safety Review Board (SRB) Executive Session',
    description: 'Quarterly review of SPIs, high-risk hazard mitigation, and flight ops safety performance.',
    status: 'SCHEDULED',
    confidentiality_level: 'INTERNAL',
    target_department: 'Safety & Flight Operations',
    planned_start_time: '2026-05-15 10:00:00',
    planned_end_time: '2026-05-15 12:00:00',
    location: 'HQ Executive Boardroom A',
    is_virtual: false,
    organizer_id: 'USR-FO-01',
    organizer_name: 'Captain Burak Demir',
    chair_id: 'USR-AM-01',
    chair_name: 'Cemil Demir (Accountable Manager)',
    quorum_required: true,
    quorum_achieved: true,
    is_management_review: false,
    is_closed: false,
    version: 1,
    created_at: '2026-04-01T09:00:00.000Z',
    updated_at: '2026-04-01T09:00:00.000Z'
  },
  {
    id: 'MTG-2026-002',
    organization_id: 'ORG-XYZ',
    meeting_number: 'MTG-2026-002',
    meeting_type_id: 'MT-SAG',
    title: 'Flight Operations & Ground Handling Safety Action Group',
    description: 'Monthly review of ramp safety findings, turnaround hazard reports, and turnaround SOP compliance.',
    status: 'COMPLETED',
    confidentiality_level: 'INTERNAL',
    target_department: 'Flight Operations & Ground Ops',
    planned_start_time: '2026-04-10 14:00:00',
    planned_end_time: '2026-04-10 15:30:00',
    actual_start_time: '2026-04-10 14:05:00',
    actual_end_time: '2026-04-10 15:25:00',
    location: 'Flight Operations Briefing Room 2',
    is_virtual: true,
    video_conference_url: 'https://meet.luminixaviation.com/sag-ops',
    organizer_id: 'USR-FO-01',
    organizer_name: 'Captain Burak Demir',
    chair_id: 'USR-SO-01',
    chair_name: 'Erdal Aydeniz (Safety Manager)',
    quorum_required: false,
    quorum_achieved: true,
    is_management_review: false,
    is_closed: true,
    version: 2,
    created_at: '2026-03-25T11:00:00.000Z',
    updated_at: '2026-04-10T16:00:00.000Z'
  },
  {
    id: 'MTG-2026-003',
    organization_id: 'ORG-XYZ',
    meeting_number: 'MTG-2026-003',
    meeting_type_id: 'MT-MR',
    title: 'Annual EASA/ISO Management Review & SMS Audit Board',
    description: 'Comprehensive annual management review covering quality objectives, audit outcomes, and compliance monitoring.',
    status: 'IN_PROGRESS',
    confidentiality_level: 'CONFIDENTIAL',
    target_department: 'Quality & Compliance Oversight',
    planned_start_time: '2026-05-20 09:30:00',
    planned_end_time: '2026-05-20 13:00:00',
    location: 'Luminix HQ Main Auditorium',
    is_virtual: false,
    organizer_id: 'USR-QM-01',
    organizer_name: 'Selin Ozturk (Quality Manager)',
    chair_id: 'USR-AM-01',
    chair_name: 'Cemil Demir (Accountable Manager)',
    quorum_required: true,
    quorum_achieved: true,
    is_management_review: true,
    is_closed: false,
    version: 1,
    created_at: '2026-04-05T08:30:00.000Z',
    updated_at: '2026-04-05T08:30:00.000Z'
  }
];

const FALLBACK_KPIS: MeetingDashboardKPIs = {
  total_meetings: 3,
  scheduled_meetings: 1,
  completed_meetings: 1,
  in_progress_meetings: 1,
  management_reviews_count: 1,
  total_decisions_count: 8,
  linked_central_actions_count: 12,
  open_meeting_actions_count: 3,
  average_attendance_rate: 94.2,
  quorum_achievement_rate: 100.0
};

const FALLBACK_PARTICIPANTS: Record<string, MeetingParticipant[]> = {};
const FALLBACK_AGENDA_ITEMS: Record<string, MeetingAgendaItem[]> = {};
const FALLBACK_ACTIONS: Record<string, MeetingActionBridge[]> = {};

export const meetingsService = {
  async getDashboardKPIs(): Promise<MeetingDashboardKPIs> {
    try {
      const data = await apiRequest<MeetingDashboardKPIs>('/meetings/analytics/dashboard');
      if (data && typeof data === 'object' && typeof data.total_meetings === 'number') {
        return data;
      }
      return FALLBACK_KPIS;
    } catch (err) {
      console.warn('[meetingsService] getDashboardKPIs network error, using fallback:', err);
      return FALLBACK_KPIS;
    }
  },

  async getMeetingTypes(): Promise<MeetingType[]> {
    try {
      const data = await apiRequest<MeetingType[]>('/meetings/types');
      if (Array.isArray(data) && data.length > 0) return data;
      return FALLBACK_MEETING_TYPES;
    } catch (err) {
      console.warn('[meetingsService] getMeetingTypes network error, using fallback:', err);
      return FALLBACK_MEETING_TYPES;
    }
  },

  async createMeetingType(payload: Partial<MeetingType>): Promise<MeetingType> {
    try {
      const data = await apiRequest<MeetingType>('/meetings/types', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (data && data.id) {
        FALLBACK_MEETING_TYPES.push(data);
        return data;
      }
    } catch (err) {
      console.warn('[meetingsService] createMeetingType fallback:', err);
    }
    const newType: MeetingType = {
      id: payload.id || `MT-${Date.now()}`,
      organization_id: 'ORG-XYZ',
      type_code: payload.type_code || `TYPE-${Date.now().toString().slice(-4)}`,
      title: payload.title || 'Yeni Toplantı Türü',
      category: payload.category || 'GOVERNANCE',
      default_duration_minutes: payload.default_duration_minutes || 60,
      requires_agenda: payload.requires_agenda ?? true,
      requires_minutes: payload.requires_minutes ?? true,
      requires_quorum: Boolean(payload.requires_quorum),
      quorum_percentage: payload.quorum_percentage || 50,
      is_management_review: Boolean(payload.is_management_review),
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    FALLBACK_MEETING_TYPES.push(newType);
    return newType;
  },

  async updateMeetingType(id: string, payload: Partial<MeetingType>): Promise<MeetingType> {
    try {
      const data = await apiRequest<MeetingType>(`/meetings/types/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      if (data) return data;
    } catch (err) {
      console.warn('[meetingsService] updateMeetingType fallback:', err);
    }
    const found = FALLBACK_MEETING_TYPES.find(t => t.id === id);
    if (found) {
      Object.assign(found, payload, { updated_at: new Date().toISOString() });
      return found;
    }
    throw new Error('Toplantı türü bulunamadı');
  },

  async deleteMeetingType(id: string): Promise<boolean> {
    try {
      await apiRequest(`/meetings/types/${id}`, { method: 'DELETE' });
    } catch (err) {
      console.warn('[meetingsService] deleteMeetingType fallback:', err);
    }
    const idx = FALLBACK_MEETING_TYPES.findIndex(t => t.id === id);
    if (idx !== -1) {
      FALLBACK_MEETING_TYPES[idx].is_active = false;
    }
    return true;
  },

  async getSystemUsers(search?: string): Promise<SystemUserOption[]> {
    try {
      const q = search ? `?search=${encodeURIComponent(search)}` : '';
      const data = await apiRequest<SystemUserOption[]>(`/meetings/system-users${q}`);
      if (Array.isArray(data) && data.length > 0) return data;
    } catch (err) {
      try {
        const adminUsers = await apiRequest<any[]>('/v1/admin/users');
        if (Array.isArray(adminUsers) && adminUsers.length > 0) {
          return adminUsers.map(u => ({
            id: String(u.id),
            username: u.username,
            display_name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username,
            email: u.email || `${u.username}@luminixaviation.com`,
            role: (u.roles && u.roles[0]) || 'USER',
            department: 'Operations'
          }));
        }
      } catch {}
    }
    if (search) {
      const s = search.toLowerCase();
      return FALLBACK_SYSTEM_USERS.filter(u =>
        u.display_name.toLowerCase().includes(s) ||
        u.username.toLowerCase().includes(s) ||
        u.email.toLowerCase().includes(s) ||
        u.role.toLowerCase().includes(s)
      );
    }
    return FALLBACK_SYSTEM_USERS;
  },

  async getMeetings(filters?: {
    status?: string;
    meeting_type_id?: string;
    target_department?: string;
    search?: string;
    user_id?: string;
  }): Promise<Meeting[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.meeting_type_id) params.append('meeting_type_id', filters.meeting_type_id);
      if (filters?.target_department) params.append('target_department', filters.target_department);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.user_id) params.append('user_id', filters.user_id);

      const qs = params.toString();
      const endpoint = qs ? `/meetings?${qs}` : '/meetings';
      const data = await apiRequest<Meeting[]>(endpoint);
      if (Array.isArray(data)) return data;
      return FALLBACK_MEETINGS;
    } catch (err) {
      console.warn('[meetingsService] getMeetings network error, using fallback:', err);
      let list = [...FALLBACK_MEETINGS];
      if (filters?.status) list = list.filter(m => m.status === filters.status);
      if (filters?.meeting_type_id) list = list.filter(m => m.meeting_type_id === filters.meeting_type_id);
      if (filters?.search) {
        const s = filters.search.toLowerCase();
        list = list.filter(m => m.title.toLowerCase().includes(s) || m.meeting_number.toLowerCase().includes(s));
      }
      return list;
    }
  },

  async getMeetingById(id: string): Promise<Meeting> {
    try {
      const data = await apiRequest<Meeting>(`/meetings/${id}`);
      if (data && data.id) return data;
    } catch (err) {
      console.warn('[meetingsService] getMeetingById error, checking fallback:', err);
    }
    const found = FALLBACK_MEETINGS.find(m => m.id === id);
    if (found) return found;
    return FALLBACK_MEETINGS[0];
  },

  async createMeeting(payload: Partial<Meeting>): Promise<Meeting> {
    try {
      const data = await apiRequest<Meeting>('/meetings', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (data && data.id) {
        FALLBACK_MEETINGS.unshift(data);
        return data;
      }
    } catch (err) {
      console.warn('[meetingsService] createMeeting network error, storing in local fallback:', err);
    }
    const year = new Date().getFullYear();
    const newMeeting: Meeting = {
      id: payload.id || `MTG-${Date.now()}`,
      organization_id: 'ORG-XYZ',
      meeting_number: payload.meeting_number || `MTG-${year}-${String(FALLBACK_MEETINGS.length + 1).padStart(3, '0')}`,
      meeting_type_id: payload.meeting_type_id || 'MT-SRB',
      title: payload.title || 'Yeni Toplantı',
      description: payload.description || null,
      status: payload.status || 'SCHEDULED',
      confidentiality_level: payload.confidentiality_level || 'INTERNAL',
      target_department: payload.target_department || 'Safety & Flight Operations',
      planned_start_time: payload.planned_start_time || new Date().toISOString(),
      planned_end_time: payload.planned_end_time || new Date(Date.now() + 7200000).toISOString(),
      location: payload.location || 'HQ Main Executive Conference Room',
      is_virtual: Boolean(payload.is_virtual),
      video_conference_url: payload.video_conference_url || null,
      organizer_id: payload.organizer_id || 'USR-CURRENT',
      organizer_name: payload.organizer_name || 'Current User',
      chair_id: payload.chair_id || 'USR-AM-01',
      chair_name: payload.chair_name || 'Cemil Demir (Accountable Manager)',
      quorum_required: Boolean(payload.quorum_required),
      quorum_achieved: true,
      is_management_review: Boolean(payload.is_management_review),
      is_closed: false,
      version: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    FALLBACK_MEETINGS.unshift(newMeeting);
    return newMeeting;
  },

  async updateMeeting(id: string, payload: Partial<Meeting>): Promise<Meeting> {
    try {
      return await apiRequest<Meeting>(`/meetings/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.warn('[meetingsService] updateMeeting network error:', err);
      const found = FALLBACK_MEETINGS.find(m => m.id === id);
      if (found) {
        Object.assign(found, payload, { updated_at: new Date().toISOString() });
        return found;
      }
      throw err;
    }
  },

  async transitionStatus(id: string, toStatus: string, reason: string): Promise<Meeting> {
    try {
      return await apiRequest<Meeting>(`/meetings/${id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ to_status: toStatus, reason })
      });
    } catch (err) {
      console.warn('[meetingsService] transitionStatus fallback:', err);
      const found = FALLBACK_MEETINGS.find(m => m.id === id);
      if (found) {
        (found as any).status = toStatus;
        return found;
      }
      throw err;
    }
  },

  async getStatusHistory(id: string): Promise<MeetingStatusHistory[]> {
    try {
      const data = await apiRequest<MeetingStatusHistory[]>(`/meetings/${id}/status-history`);
      if (Array.isArray(data)) return data;
      return [];
    } catch {
      return [];
    }
  },

  async getVersions(id: string): Promise<MeetingVersion[]> {
    try {
      const data = await apiRequest<MeetingVersion[]>(`/meetings/${id}/versions`);
      if (Array.isArray(data)) return data;
      return [];
    } catch {
      return [];
    }
  },

  async getParticipants(id: string): Promise<MeetingParticipant[]> {
    try {
      const data = await apiRequest<MeetingParticipant[]>(`/meetings/${id}/participants`);
      if (Array.isArray(data)) return data;
    } catch {}
    return FALLBACK_PARTICIPANTS[id] || [];
  },

  async addParticipant(id: string, payload: Partial<MeetingParticipant>): Promise<MeetingParticipant> {
    try {
      const data = await apiRequest<MeetingParticipant>(`/meetings/${id}/participants`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (data && data.id) {
        if (!FALLBACK_PARTICIPANTS[id]) FALLBACK_PARTICIPANTS[id] = [];
        FALLBACK_PARTICIPANTS[id].push(data);
        return data;
      }
    } catch (err) {
      console.warn('[meetingsService] addParticipant network error, storing in local fallback:', err);
    }
    const newP: MeetingParticipant = {
      id: payload.id || `MP-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      organization_id: 'ORG-XYZ',
      meeting_id: id,
      user_id: payload.user_id || 'USR-CURRENT',
      user_name: payload.user_name || 'Katılımcı',
      email: payload.email || null,
      participant_role: payload.participant_role || 'ATTENDEE',
      is_required: payload.is_required ?? 1,
      invitation_status: payload.invitation_status || 'PENDING',
      attendance_status: 'NOT_RECORDED',
      created_at: new Date().toISOString()
    };
    if (!FALLBACK_PARTICIPANTS[id]) FALLBACK_PARTICIPANTS[id] = [];
    FALLBACK_PARTICIPANTS[id].push(newP);
    return newP;
  },

  async deleteParticipant(id: string, pId: string): Promise<boolean> {
    try {
      await apiRequest<{ success: boolean }>(`/meetings/${id}/participants/${pId}`, {
        method: 'DELETE'
      });
    } catch (err) {
      console.warn('[meetingsService] deleteParticipant network error, updating fallback:', err);
    }
    if (FALLBACK_PARTICIPANTS[id]) {
      FALLBACK_PARTICIPANTS[id] = FALLBACK_PARTICIPANTS[id].filter(p => p.id !== pId);
    }
    return true;
  },

  async updateAttendance(id: string, pId: string, status: string, delegateUserId?: string, delegateUserName?: string): Promise<MeetingParticipant> {
    try {
      return await apiRequest<MeetingParticipant>(`/meetings/${id}/participants/${pId}/attendance`, {
        method: 'PATCH',
        body: JSON.stringify({ attendance_status: status, delegate_user_id: delegateUserId, delegate_user_name: delegateUserName })
      });
    } catch {
      const list = FALLBACK_PARTICIPANTS[id] || [];
      const item = list.find(p => p.id === pId);
      if (item) {
        item.attendance_status = status as any;
        item.delegate_user_id = delegateUserId || null;
        item.delegate_user_name = delegateUserName || null;
        return item;
      }
      throw new Error('Katılımcı bulunamadı');
    }
  },

  async getAgenda(id: string): Promise<{ agenda?: MeetingAgenda; items: MeetingAgendaItem[] }> {
    try {
      const data = await apiRequest<{ agenda?: MeetingAgenda; items: MeetingAgendaItem[] }>(`/meetings/${id}/agenda`);
      if (data && Array.isArray(data.items)) {
        if (!FALLBACK_AGENDA_ITEMS[id] || FALLBACK_AGENDA_ITEMS[id].length === 0) {
          FALLBACK_AGENDA_ITEMS[id] = data.items;
        }
        return data;
      }
    } catch {}
    return { items: FALLBACK_AGENDA_ITEMS[id] || [] };
  },

  async addAgendaItem(id: string, payload: Partial<MeetingAgendaItem>): Promise<MeetingAgendaItem> {
    try {
      const data = await apiRequest<MeetingAgendaItem>(`/meetings/${id}/agenda/items`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (data && data.id) {
        if (!FALLBACK_AGENDA_ITEMS[id]) FALLBACK_AGENDA_ITEMS[id] = [];
        FALLBACK_AGENDA_ITEMS[id].push(data);
        return data;
      }
    } catch (err) {
      console.warn('[meetingsService] addAgendaItem network error, storing in fallback:', err);
    }
    const currentList = FALLBACK_AGENDA_ITEMS[id] || [];
    const order = currentList.length + 1;
    const newItem: MeetingAgendaItem = {
      id: payload.id || `MAI-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      organization_id: 'ORG-XYZ',
      agenda_id: `MAG-${id}`,
      meeting_id: id,
      parent_item_id: payload.parent_item_id || null,
      item_order: order,
      item_code: payload.item_code || `AG-${String(order).padStart(2, '0')}`,
      title: payload.title || 'Gündem Maddesi',
      description: payload.description || null,
      presenter_id: payload.presenter_id || null,
      presenter_name: payload.presenter_name || null,
      duration_minutes: payload.duration_minutes || 15,
      is_discussion: payload.is_discussion ?? 1,
      is_decision_required: payload.is_decision_required ?? 0,
      is_action_required: payload.is_action_required ?? 0,
      item_status: (payload.item_status as any) || 'PENDING',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    if (!FALLBACK_AGENDA_ITEMS[id]) FALLBACK_AGENDA_ITEMS[id] = [];
    FALLBACK_AGENDA_ITEMS[id].push(newItem);
    return newItem;
  },

  async updateAgendaItem(id: string, itemId: string, payload: Partial<MeetingAgendaItem>): Promise<MeetingAgendaItem> {
    try {
      const data = await apiRequest<MeetingAgendaItem>(`/meetings/${id}/agenda/items/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      if (data && data.id) {
        const list = FALLBACK_AGENDA_ITEMS[id] || [];
        const idx = list.findIndex(i => i.id === itemId);
        if (idx !== -1) list[idx] = data;
        return data;
      }
    } catch (err) {
      console.warn('[meetingsService] updateAgendaItem network error:', err);
    }
    const list = FALLBACK_AGENDA_ITEMS[id] || [];
    const item = list.find(i => i.id === itemId);
    if (item) {
      Object.assign(item, payload, { updated_at: new Date().toISOString() });
      return item;
    }
    throw new Error('Gündem maddesi bulunamadı');
  },

  async deleteAgendaItem(id: string, itemId: string): Promise<boolean> {
    try {
      await apiRequest<{ success: boolean }>(`/meetings/${id}/agenda/items/${itemId}`, {
        method: 'DELETE'
      });
    } catch (err) {
      console.warn('[meetingsService] deleteAgendaItem network error:', err);
    }
    if (FALLBACK_AGENDA_ITEMS[id]) {
      FALLBACK_AGENDA_ITEMS[id] = FALLBACK_AGENDA_ITEMS[id].filter(i => i.id !== itemId);
    }
    return true;
  },

  async getMinutes(id: string): Promise<MeetingMinutes | null> {
    try {
      return await apiRequest<MeetingMinutes | null>(`/meetings/${id}/minutes`);
    } catch {
      return null;
    }
  },

  async createMinutes(id: string, payload: any): Promise<MeetingMinutes> {
    return apiRequest<MeetingMinutes>(`/meetings/${id}/minutes`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  async approveMinutes(id: string, approverId: string, approverName: string, notes?: string): Promise<MeetingMinutes> {
    return apiRequest<MeetingMinutes>(`/meetings/${id}/minutes/approve`, {
      method: 'POST',
      body: JSON.stringify({ approver_id: approverId, approver_name: approverName, notes })
    });
  },

  async getDecisions(id: string): Promise<MeetingDecision[]> {
    try {
      const data = await apiRequest<MeetingDecision[]>(`/meetings/${id}/decisions`);
      if (Array.isArray(data)) return data;
      return [];
    } catch {
      return [];
    }
  },

  async recordDecision(id: string, payload: Partial<MeetingDecision>): Promise<MeetingDecision> {
    return apiRequest<MeetingDecision>(`/meetings/${id}/decisions`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  async getLinkedActions(id: string): Promise<MeetingActionBridge[]> {
    try {
      const data = await apiRequest<MeetingActionBridge[]>(`/meetings/${id}/actions`);
      if (Array.isArray(data)) {
        if (!FALLBACK_ACTIONS[id] || FALLBACK_ACTIONS[id].length === 0) {
          FALLBACK_ACTIONS[id] = data;
        }
        return data;
      }
    } catch {}
    return FALLBACK_ACTIONS[id] || [];
  },

  async linkCentralAction(id: string, payload: any): Promise<MeetingActionBridge> {
    try {
      const data = await apiRequest<MeetingActionBridge>(`/meetings/${id}/actions`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (data && data.id) {
        if (!FALLBACK_ACTIONS[id]) FALLBACK_ACTIONS[id] = [];
        FALLBACK_ACTIONS[id].push(data);
        return data;
      }
    } catch (err) {
      console.warn('[meetingsService] linkCentralAction network error, storing in fallback:', err);
    }
    const actionCode = payload.action_code || `ACT-MTG-${Date.now().toString().slice(-4)}`;
    const newAction: MeetingActionBridge = {
      id: `MACT-${Date.now()}`,
      organization_id: 'ORG-XYZ',
      meeting_id: id,
      decision_id: payload.decision_id || null,
      agenda_item_id: payload.agenda_item_id || null,
      central_action_id: payload.central_action_id || `CENTRAL-${actionCode}`,
      action_code: actionCode,
      title: payload.title || 'Toplantı Eylemi',
      action_type: payload.action_type || 'CORRECTIVE',
      assigned_to_id: payload.assigned_to_id || 'usr_erdal_aydeniz',
      assigned_to_name: payload.assigned_to_name || 'Erdal Aydeniz',
      due_date: payload.due_date || new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0],
      status: 'OPEN',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    if (!FALLBACK_ACTIONS[id]) FALLBACK_ACTIONS[id] = [];
    FALLBACK_ACTIONS[id].push(newAction);
    return newAction;
  },

  async updateActionStatus(id: string, actId: string, status: string, evidenceRef?: string): Promise<MeetingActionBridge> {
    try {
      return await apiRequest<MeetingActionBridge>(`/meetings/${id}/actions/${actId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status, closure_evidence_ref: evidenceRef })
      });
    } catch (err) {
      console.warn('[meetingsService] updateActionStatus fallback:', err);
      const list = FALLBACK_ACTIONS[id] || [];
      const item = list.find(a => a.id === actId);
      if (item) {
        item.status = status as any;
        if (evidenceRef) item.closure_evidence_ref = evidenceRef;
        item.updated_at = new Date().toISOString();
        return item;
      }
      throw err;
    }
  },

  async getManagementReviews(): Promise<ManagementReview[]> {
    try {
      const data = await apiRequest<ManagementReview[]>('/meetings/management-reviews');
      if (Array.isArray(data)) return data;
      return [];
    } catch {
      return [];
    }
  },

  async getManagementReviewById(mrId: string): Promise<{ review: ManagementReview; inputs: ManagementReviewInput[] }> {
    return apiRequest<{ review: ManagementReview; inputs: ManagementReviewInput[] }>(`/meetings/management-reviews/${mrId}`);
  },

  async createManagementReview(payload: Partial<ManagementReview>): Promise<ManagementReview> {
    return apiRequest<ManagementReview>('/meetings/management-reviews', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  async closeMeeting(id: string, closedByName?: string): Promise<Meeting> {
    return apiRequest<Meeting>(`/meetings/${id}/close`, {
      method: 'POST',
      body: JSON.stringify({ closed_by_name: closedByName })
    });
  }
};
